import React, { createContext, useCallback, useRef, useContext } from 'react';
import { CHART_ZOOM_RESET_ALL } from '../hooks/useChartZoom';

type ResetFn = () => void;

interface ChartZoomContextValue {
  register: (id: string, reset: ResetFn) => void;
  unregister: (id: string) => void;
  resetAll: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
}

const ChartZoomContext = createContext<ChartZoomContextValue | null>(null);

const CHART_ZOOM_RESET = 'chartZoomReset';
const CHART_ZOOM_IN = 'chartZoomIn';
const CHART_ZOOM_OUT = 'chartZoomOut';

export function ChartZoomProvider({ children }: { children: React.ReactNode }) {
  const registry = useRef<Map<string, ResetFn>>(new Map());

  const register = useCallback((id: string, reset: ResetFn) => {
    registry.current.set(id, reset);
  }, []);

  const unregister = useCallback((id: string) => {
    registry.current.delete(id);
  }, []);

  const resetAll = useCallback(() => {
    registry.current.forEach((reset) => {
      try {
        reset();
      } catch (e) {
        console.warn('ChartZoomContext: reset failed', e);
      }
    });
    window.dispatchEvent(new CustomEvent(CHART_ZOOM_RESET));
    window.dispatchEvent(new CustomEvent(CHART_ZOOM_RESET_ALL));
  }, []);

  const zoomIn = useCallback(() => {
    window.dispatchEvent(new CustomEvent(CHART_ZOOM_IN, { detail: { factor: 0.9 } }));
  }, []);

  const zoomOut = useCallback(() => {
    window.dispatchEvent(new CustomEvent(CHART_ZOOM_OUT, { detail: { factor: 1.1 } }));
  }, []);

  const value: ChartZoomContextValue = {
    register,
    unregister,
    resetAll,
    zoomIn,
    zoomOut
  };

  return (
    <ChartZoomContext.Provider value={value}>
      {children}
    </ChartZoomContext.Provider>
  );
}

export function useChartZoom() {
  const ctx = useContext(ChartZoomContext);
  return ctx;
}

export { CHART_ZOOM_RESET, CHART_ZOOM_IN, CHART_ZOOM_OUT };
